import { Link } from "react-router-dom";
import { Download, Globe, Smartphone, Share, PlusSquare, Copy } from "lucide-react";
import { toast } from "sonner";

const SHARE_URL = `${window.location.origin}/download`;

const copyLink = async () => {
  try {
    if (navigator.share) await navigator.share({ title: "Aphelion", text: "Reach me on Aphelion — zero-knowledge encrypted messaging", url: SHARE_URL });
    else { await navigator.clipboard.writeText(SHARE_URL); toast.success("Link copied"); }
  } catch { /* share cancelled */ }
};

function Card({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-nebula border border-rim rounded-secure p-6 hover:border-stellar/30 transition-colors">
      <div className="flex items-center gap-3 mb-3"><Icon size={20} strokeWidth={1.5} className="text-stellar" /><h2 className="font-branding text-base md:text-lg font-semibold">{title}</h2></div>
      {children}
    </div>
  );
}

export default function DownloadPage() {
  return (
    <div className="min-h-[100dvh] bg-void starfield text-ash" data-testid="download-page">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="flex items-center gap-4 mb-10">
          <img src="/icon-180.png" alt="Aphelion" className="w-16 h-16 rounded-secure border border-glass shadow-stellar-glow" />
          <p className="font-branding tracking-[0.3em] font-bold text-xl">APHELION</p>
        </div>
        <h1 className="font-branding text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">Get Aphelion</h1>
        <p className="text-ash/55 mb-10 max-w-xl">Zero-knowledge messaging. No email, no phone number — just keys. Burn-on-read timers and a Decoy Vault for plausible deniability.</p>
        <div className="grid gap-5 md:grid-cols-2 mb-10">
          <Card icon={Smartphone} title="Android" testid="download-android-card">
            <p className="text-sm text-ash/55 mb-4">Download the app, open the file and allow “Install unknown apps” if asked. Have an older version installed? Uninstall it first.</p>
            <a href="/aphelion.apk" download="Aphelion.apk" data-testid="download-apk-btn" className="flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-3 hover:opacity-85 transition-opacity"><Download size={16} strokeWidth={1.5} /> Download APK</a>
          </Card>
          <Card icon={Share} title="iPhone" testid="download-ios-card">
            <ol className="text-sm text-ash/70 space-y-2 mb-4">
              <li className="flex items-center gap-2"><Globe size={16} strokeWidth={1.5} className="text-stellar" /> Open this page in Safari</li>
              <li className="flex items-center gap-2"><Share size={16} strokeWidth={1.5} className="text-stellar" /> Tap the Share button</li>
              <li className="flex items-center gap-2"><PlusSquare size={16} strokeWidth={1.5} className="text-stellar" /> Choose “Add to Home Screen”</li>
            </ol>
            <Link to="/" data-testid="download-ios-open-btn" className="flex items-center justify-center border border-rim rounded-secure py-3 text-sm hover:border-stellar/40 transition-colors">Open Aphelion</Link>
          </Card>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-6 bg-nebula border border-rim rounded-secure p-6">
          <img src="/store/download-qr.png" alt="QR code" className="w-36 h-36 rounded-secure" data-testid="download-qr" />
          <div className="flex-1 text-center sm:text-left">
            <h2 className="font-branding text-base md:text-lg font-semibold mb-1">Share Aphelion</h2>
            <p className="text-sm text-ash/55 mb-3">Scan the code or send this link:</p>
            <p className="font-mono text-sm text-stellar break-all mb-4" data-testid="download-share-url">{SHARE_URL}</p>
            <button onClick={copyLink} data-testid="download-share-btn" className="inline-flex items-center gap-2 border border-stellar/40 text-stellar rounded-secure px-5 py-2.5 text-sm hover:shadow-stellar-glow transition-shadow"><Copy size={16} strokeWidth={1.5} /> Share link</button>
          </div>
        </div>
        <div className="flex gap-6 mt-10 text-xs text-ash/40">
          <Link to="/" className="hover:text-stellar" data-testid="download-web-link">Use in browser</Link>
          <Link to="/privacy" className="hover:text-stellar" data-testid="download-privacy-link">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
